/** @jsxImportSource @emotion/react */
import { Global, Theme, css, useTheme } from '@emotion/react';

import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useCallback, useEffect, useState } from 'react';

import homeIcon from '@public/icons/bottomTab/ic_bottom_tab_home.png';
import libraryIcon from '@public/icons/bottomTab/ic_bottom_tab_library.png';
import playIcon from '@public/icons/bottomTab/ic_bottom_tab_play.png';
import studioIcon from '@public/icons/bottomTab/ic_bottom_tab_studio.png';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { AppSpacing, AppTypography } from '@styles/appDesignTokens';
import { media } from '@styles/css';

/** 피드 아이콘 — 카드/글 목록 형태 */
const FeedIcon = ({ color = '#BDBDBD' }: { color?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

type NavItem = {
  path: string;
  label: string;
  icon?: StaticImageData;
  // eslint-disable-next-line no-undef
  svgIcon?: (props: { color?: string }) => JSX.Element;
};

const NAV_ITEMS: NavItem[] = [
  { path: '/', icon: homeIcon, label: '홈' },
  { path: '/feeds', svgIcon: FeedIcon, label: '피드' },
  { path: '/play', icon: playIcon, label: '플레이' },
  { path: '/library', icon: libraryIcon, label: '라이브러리' },
  { path: '/studio', icon: studioIcon, label: '마이페이지' },
];

/** 회색 아이콘 → 채티 청록색(#20a2ae)으로 변환하는 CSS filter */
const chatieActiveFilter =
  'brightness(0) saturate(100%) invert(53%) sepia(60%) saturate(500%) hue-rotate(145deg) brightness(95%) contrast(90%)';

export const SIDEBAR_WIDTH_COLLAPSED = 72;
export const SIDEBAR_WIDTH_EXPANDED = 180;

const sidebarCSS = (theme: Theme) => css`
  display: none;

  ${media.tablet} {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${SIDEBAR_WIDTH_COLLAPSED}px;
    background-color: ${theme.var.white};
    border-right: 1px solid ${theme.var.gray200};
    padding-top: ${AppSpacing.xlarge}px;
    z-index: 9000;
    align-items: center;
    gap: ${AppSpacing.xsmall}px;
    transition: width 0.2s ease;
    overflow: hidden;
  }
`;

const sidebarExpandedCSS = css`
  ${media.tablet} {
    width: ${SIDEBAR_WIDTH_EXPANDED}px;
    align-items: flex-start;
    padding-left: ${AppSpacing.small}px;
    padding-right: ${AppSpacing.small}px;
  }
`;

const toggleButtonCSS = (theme: Theme) => css`
  display: none;

  ${media.tablet} {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid ${theme.var.gray200};
    background-color: ${theme.var.white};
    cursor: pointer;
    margin-bottom: ${AppSpacing.small}px;
    transition: transform 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background-color: ${theme.colors.main50};
    }

    svg {
      width: 16px;
      height: 16px;
      color: ${theme.colors.main500};
    }
  }
`;

const toggleExpandedCSS = css`
  ${media.tablet} {
    align-self: flex-end;
  }
`;

const sidebarButtonCSS = (isActive: boolean) => (theme: Theme) =>
  css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 0.15s;
    flex-shrink: 0;

    ${isActive &&
    css`
      background-color: ${theme.colors.main50};
    `}

    &:hover {
      background-color: ${theme.colors.main50};
    }

    &:active {
      background-color: ${theme.colors.main100};
    }

    img {
      height: 24px;
      flex-shrink: 0;
      ${isActive &&
      css`
        filter: ${chatieActiveFilter};
      `}
    }

    .nav-label {
      font-size: 0.625rem;
      margin-top: 2px;
      color: ${isActive ? theme.colors.main500 : theme.var.gray400};
      white-space: nowrap;
    }
  `;

const sidebarButtonExpandedCSS = (isActive: boolean) => (theme: Theme) =>
  css`
    ${media.tablet} {
      flex-direction: row;
      width: 100%;
      height: 44px;
      padding: 0 ${AppSpacing.small}px;
      justify-content: flex-start;
      gap: ${AppSpacing.small}px;
      border-radius: 8px;

      .nav-label {
        ${AppTypography.body14};
        font-weight: ${isActive ? 600 : 400};
        margin-top: 0;
        color: ${isActive ? theme.colors.main500 : theme.var.gray700};
      }
    }
  `;

const ChevronIcon = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: direction === 'left' ? 'rotate(180deg)' : 'none',
      transition: 'transform 0.2s ease',
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const getActiveIndex = (pathname: string) =>
  NAV_ITEMS.findIndex((item) => item.path === pathname);

const BottomTabChatie = () => {
  const router = useRouter();
  const { platform } = useGlobalContext();
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(() =>
    getActiveIndex(router.pathname)
  );
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setActiveIndex(getActiveIndex(router.pathname));
  }, [router.pathname]);

  const toggleSidebar = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (platform === Platform.web && activeIndex !== -1) {
      document.body.classList.add('has-sidebar');
      return () => {
        document.body.classList.remove('has-sidebar');
      };
    }
  }, [platform, activeIndex]);

  if (platform !== Platform.web) {
    return null;
  }

  if (activeIndex === -1) {
    return null;
  }

  return (
    <>
      <Global
        styles={css`
          :root {
            --sidebar-width: 0px;
          }
          ${media.tablet} {
            :root {
              --sidebar-width: ${isExpanded
                ? SIDEBAR_WIDTH_EXPANDED
                : SIDEBAR_WIDTH_COLLAPSED}px;
            }
            body {
              padding-left: var(--sidebar-width);
              transition: padding-left 0.2s ease;
            }
          }
        `}
      />
      <nav css={[sidebarCSS, isExpanded && sidebarExpandedCSS]}>
        <button
          css={[toggleButtonCSS, isExpanded && toggleExpandedCSS]}
          onClick={toggleSidebar}
          aria-label={isExpanded ? '사이드바 접기' : '사이드바 펼치기'}
        >
          <ChevronIcon direction={isExpanded ? 'left' : 'right'} />
        </button>

        {NAV_ITEMS.map((item, index) => {
          const isActive = activeIndex === index;
          return (
            <Link key={item.label} href={item.path} prefetch={false}>
              <a
                css={[
                  sidebarButtonCSS(isActive),
                  isExpanded && sidebarButtonExpandedCSS(isActive),
                ]}
              >
                {item.svgIcon ? (
                  <item.svgIcon
                    color={isActive ? theme.colors.main500 : '#BDBDBD'}
                  />
                ) : (
                  <Image
                    width={24}
                    height={24}
                    src={item.icon!}
                    loader={({ src }) => src}
                    alt={item.label}
                  />
                )}
                <span className="nav-label">{item.label}</span>
              </a>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default BottomTabChatie;
